import { ChangeEvent, KeyboardEvent, RefObject, useCallback, useRef, useState } from 'react';
import { roundToDecimalIfNotWhole } from '@/utils/numbers';

interface UseNumberInputProps {
  value?: number;
  inputRef: RefObject<HTMLInputElement | null>;
  onSubmit: (value: number) => void;
  parse?: (value: number) => number;
  format?: (value: number) => number;
  decimals?: 0 | 1 | 2;
  blurOnEnter?: boolean;
  placeholder?: string;
  displaySuffix?: string;
  // When true, blur/Enter commit only if the user actually edited the field this focus. Prevents a
  // bare focus+blur from re-writing the (rounded) displayed value — which for a two-way-bound field
  // both quantizes the stored value and can clobber a concurrent external update on blur.
  commitOnlyWhenEdited?: boolean;
}
export function useNumberInput({
  inputRef,
  value,
  onSubmit,
  parse,
  format,
  decimals,
  blurOnEnter,
  placeholder,
  displaySuffix,
  commitOnlyWhenEdited,
}: UseNumberInputProps) {
  const [tempValue, setTempValue] = useState<string>('');
  const [hasFocus, setHasFocus] = useState(false);
  const editedRef = useRef(false);

  const formatDisplayValue = useCallback(
    (value?: number) => {
      if (value === undefined || value === null || typeof value === 'string') return '';
      if (format) {
        return roundToDecimalIfNotWhole(format(Number(value)), decimals);
      } else {
        return roundToDecimalIfNotWhole(Number(value), decimals);
      }
    },
    [decimals, format],
  );

  const resetValue = useCallback(() => {
    setTempValue(placeholder ? '' : formatDisplayValue(value));
  }, [value, formatDisplayValue, placeholder]);

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    editedRef.current = true;
    setTempValue(e.target.value);
  }, []);

  const handleSubmit = useCallback(
    (val: string) => {
      const parsedValue = val !== '' && !Number.isNaN(Number(val)) ? Number(val) : null;
      if (parsedValue !== null) {
        const valueToSubmit = parse ? parse(parsedValue) : parsedValue;
        onSubmit(valueToSubmit);
        setTempValue(formatDisplayValue(valueToSubmit));
      } else {
        resetValue();
      }
    },
    [formatDisplayValue, onSubmit, parse, resetValue],
  );

  const handleKeyDown = useCallback(
    (evt: KeyboardEvent<HTMLInputElement>) => {
      if (evt.code === 'Enter') {
        if (!commitOnlyWhenEdited || editedRef.current) {
          handleSubmit(tempValue);
        }
        if (blurOnEnter) {
          evt.preventDefault();
          inputRef.current?.blur();
        }
      } else if (evt.code === 'Escape') {
        // press Escape -> cancel edit
        resetValue();
        setTimeout(() => inputRef.current?.blur());
      } else if (evt.code === 'z' && evt.getModifierState('Control')) {
        // stop Cmd+z propagation
        evt.stopPropagation();
        evt.preventDefault();
      } else if (evt.code === 'Backspace' || evt.code === 'Delete') {
        evt.stopPropagation();
      }
    },
    [handleSubmit, tempValue, blurOnEnter, inputRef, resetValue, commitOnlyWhenEdited],
  );

  const handleFocus = useCallback(() => {
    setHasFocus(true);
    editedRef.current = false;
    if (placeholder) {
      setTempValue('');
    } else {
      resetValue();
    }
    setTimeout(() => inputRef.current?.select());
  }, [inputRef, resetValue, placeholder]);

  const handleBlur = useCallback(() => {
    setHasFocus(false);
    if (commitOnlyWhenEdited && !editedRef.current) {
      setTempValue('');
      return;
    }
    if (tempValue === '' && placeholder) {
      setTempValue('');
      return;
    }
    handleSubmit(tempValue);
    setTempValue('');
  }, [handleSubmit, tempValue, placeholder, commitOnlyWhenEdited]);

  const formattedValue = formatDisplayValue(value);
  const displayedValue = hasFocus
    ? tempValue
    : displaySuffix && formattedValue !== ''
      ? `${formattedValue}${displaySuffix}`
      : formattedValue;

  return {
    inputRef,
    handleBlur,
    handleFocus,
    handleKeyDown,
    handleChange,
    displayedValue,
  };
}
